// Fix 52 (2026-10-07): Reportes de ventas (pedido de la contadora con
// prototipo, 7/10/2026). Dos pestañas:
//  - Ventas por estado: mapa de cuadros, ficha de cada estado (total, pedidos,
//    clientes, % del total, puesto, ventas por día o semana, por hora y por día
//    de la semana, mejores clientes), ranking de estados y horarios de compra
//    de todo el país (para definir las horas de operación).
//  - Clientes: Top 10 / Top 20 y la lista completa con buscador, orden por
//    total y paginación.
// Las dos se descargan a Excel. Solo lectura: los cálculos viven en el
// backend (services/sales_reports_service.py).
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip,
} from 'recharts';
import {
  Loader2, FileSpreadsheet, Search, DollarSign, ShoppingBag, Users, MapPinned,
  Clock, Trophy, ArrowDown, ArrowUp, X,
} from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import KpiCard from '../components/KpiCard';
import EmptyState from '../components/EmptyState';
import UsTileMap from '../components/UsTileMap';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { formatCurrency } from '../lib/utils';

const TEXT = {
  es: {
    title: 'Reportes de ventas',
    // Fix 53 (2026-10-07): se aclara por qué puede dar más que el filtro de Órdenes.
    subtitle: 'Ventas por estado y clientes que más compran. No se cuentan las órdenes canceladas; el monto de cada orden es el mismo que la columna Total de Órdenes. Incluye las ventas de antes del arranque de la plataforma y las del día que todavía no pasaron a QuickBooks, por eso los totales pueden ser mayores que el filtro de fechas de Órdenes.',
    tabStates: 'Ventas por estado',
    tabCustomers: 'Clientes',
    presets: {
      all: 'Todo el historial', today: 'Hoy', '7d': '7 días', this_month: 'Este mes',
      last_month: 'Mes anterior', this_year: 'Este año', custom: 'Fechas',
    },
    from: 'Desde', to: 'Hasta', apply: 'Aplicar',
    period: (f, t) => (f ? `Del ${f} al ${t}` : 'Todo el historial'),
    download: 'Descargar Excel',
    downloadError: 'No se pudo descargar el Excel.',
    loading: 'Cargando…',
    error: 'No se pudo cargar el reporte.',
    kpiTotal: 'Total vendido',
    kpiOrders: 'Pedidos',
    kpiCustomers: 'Clientes',
    kpiStates: 'Estados con ventas',
    of51: (n) => `${n} de 51`,
    mapTitle: 'Mapa de ventas',
    mapHelp: 'Cuanto más oscuro, más ventas. Tocá un estado para ver su ficha.',
    less: 'Menos', more: 'Más',
    // Fix 53 (2026-10-07): casi todos son ventas viejas sin estado (medido).
    unknown: (n, total) => `${n} pedidos (${total}) no tienen un estado de los 50 + D. C.: casi todos son ventas de antes del arranque de la plataforma, cuando TikTok no mandaba el estado; el resto, direcciones fuera de los 50 estados (Puerto Rico, militares). Cuentan en el total vendido pero no en el mapa ni en el ranking.`,
    pickState: 'Tocá un estado en el mapa o en el ranking para ver su ficha.',
    // Fix 63 (2026-10-21): ventas por región
    regionsTitle: 'Ventas por región',
    regionsHelp: 'Por zona horaria. Las órdenes sin estado no entran.',
    regionNames: { east: 'East', central: 'Central', west: 'West Coast' },
    regionZones: { east: 'Hora del Este', central: 'Hora del Centro y de la Montaña', west: 'Hora del Pacífico, Alaska y Hawái' },
    regionStates: (n, of) => `${n} de ${of} estados con ventas`,
    regionTop: (code, total) => `el que más: ${code} ${total}`,
    rank: (r, of) => `Puesto ${r} de ${of}`,
    pctNational: 'del total nacional',
    salesByDay: 'Ventas por día',
    salesByWeek: 'Ventas por semana',
    salesByHour: 'Ventas por hora del día',
    salesByWeekday: 'Ventas por día de la semana',
    topCustomers: 'Mejores clientes del estado',
    noSales: 'Sin ventas en este período.',
    rankingTitle: 'Ranking de estados',
    seeAll: 'Ver todos (51)',
    seeTop: 'Ver los 10 primeros',
    state: 'Estado', total: 'Total vendido', orders: 'Pedidos', customers: 'Clientes', pct: '% del total',
    hoursTitle: 'Horarios de compra — todo el país',
    hoursHelp: (tz) => `Para definir las horas de operación. Hora de ${tz}.`,
    peakHour: (h) => `Hora con más ventas: ${h}`,
    peakDay: (d) => `Día con más ventas: ${d}`,
    seriesTitle: 'Ventas en el período — todo el país',
    weekdays: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'],
    weekdaysLong: ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'],
    weekOf: (d) => `Semana del ${d}`,
    topTitle: 'Clientes que más compran',
    top10: 'Top 10', top20: 'Top 20',
    listTitle: 'Todos los clientes',
    search: 'Buscar por nombre, perfil o teléfono',
    perPage: 'Por página',
    pageOf: (p, n) => `Página ${p} de ${n}`,
    prev: 'Anterior', next: 'Siguiente',
    colRank: '#', colName: 'Cliente', colPhone: 'Teléfono', colState: 'Estado',
    colOrders: 'Pedidos', colTotal: 'Total comprado', colLast: 'Última compra',
    pending: 'Datos pendientes',
    pendingNote: (n) => `${n} clientes todavía tienen los datos tapados por TikTok (se completan en Revisión Manual).`,
    noCustomers: 'No hay compras en este período.',
    noMatch: 'Ningún cliente coincide con la búsqueda.',
  },
  en: {
    title: 'Sales reports',
    // Fix 53 (2026-10-07)
    subtitle: 'Sales by state and top customers. Cancelled orders are not counted; each order amount is the same as the Orders Total column. Includes sales from before the platform started and today\'s orders not yet sent to QuickBooks, so totals can be higher than the Orders date filter.',
    tabStates: 'Sales by state',
    tabCustomers: 'Customers',
    presets: {
      all: 'All time', today: 'Today', '7d': '7 days', this_month: 'This month',
      last_month: 'Last month', this_year: 'This year', custom: 'Dates',
    },
    from: 'From', to: 'To', apply: 'Apply',
    period: (f, t) => (f ? `From ${f} to ${t}` : 'All time'),
    download: 'Download Excel',
    downloadError: 'The Excel file could not be downloaded.',
    loading: 'Loading…',
    error: 'The report could not be loaded.',
    kpiTotal: 'Total sales',
    kpiOrders: 'Orders',
    kpiCustomers: 'Customers',
    kpiStates: 'States with sales',
    of51: (n) => `${n} of 51`,
    mapTitle: 'Sales map',
    mapHelp: 'The darker, the more sales. Click a state to see its details.',
    less: 'Less', more: 'More',
    // Fix 53 (2026-10-07)
    unknown: (n, total) => `${n} orders (${total}) have no state among the 50 + D.C.: almost all are sales from before the platform started, when TikTok did not send the state; the rest are addresses outside the 50 states (Puerto Rico, military). They count in total sales but not on the map or the ranking.`,
    pickState: 'Click a state on the map or in the ranking to see its details.',
    // Fix 63 (2026-10-21): sales by region
    regionsTitle: 'Sales by region',
    regionsHelp: 'By time zone. Orders without a state are not included.',
    regionNames: { east: 'East', central: 'Central', west: 'West Coast' },
    regionZones: { east: 'Eastern time', central: 'Central and Mountain time', west: 'Pacific time, Alaska and Hawaii' },
    regionStates: (n, of) => `${n} of ${of} states with sales`,
    regionTop: (code, total) => `top: ${code} ${total}`,
    rank: (r, of) => `Rank ${r} of ${of}`,
    pctNational: 'of national total',
    salesByDay: 'Sales by day',
    salesByWeek: 'Sales by week',
    salesByHour: 'Sales by hour of day',
    salesByWeekday: 'Sales by day of week',
    topCustomers: 'Top customers in the state',
    noSales: 'No sales in this period.',
    rankingTitle: 'State ranking',
    seeAll: 'See all (51)',
    seeTop: 'See top 10',
    state: 'State', total: 'Total sales', orders: 'Orders', customers: 'Customers', pct: '% of total',
    hoursTitle: 'Buying hours — whole country',
    hoursHelp: (tz) => `To define operating hours. Time zone: ${tz}.`,
    peakHour: (h) => `Busiest hour: ${h}`,
    peakDay: (d) => `Busiest day: ${d}`,
    seriesTitle: 'Sales in the period — whole country',
    weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    weekdaysLong: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    weekOf: (d) => `Week of ${d}`,
    topTitle: 'Top customers',
    top10: 'Top 10', top20: 'Top 20',
    listTitle: 'All customers',
    search: 'Search by name, profile or phone',
    perPage: 'Per page',
    pageOf: (p, n) => `Page ${p} of ${n}`,
    prev: 'Previous', next: 'Next',
    colRank: '#', colName: 'Customer', colPhone: 'Phone', colState: 'State',
    colOrders: 'Orders', colTotal: 'Total spent', colLast: 'Last purchase',
    pending: 'Pending data',
    pendingNote: (n) => `${n} customers still have their data masked by TikTok (completed in Manual Review).`,
    noCustomers: 'No purchases in this period.',
    noMatch: 'No customer matches the search.',
  },
};

const STATE_PRESETS = ['today', '7d', 'this_month', 'last_month', 'this_year', 'custom'];
const CUSTOMER_PRESETS = ['all', 'today', '7d', 'this_month', 'last_month', 'this_year', 'custom'];
const PAGE_SIZES = [15, 50, 100];
const GOLD = '#D4A843';

// Fix 63 (2026-10-21): regiones de EE. UU. por zona horaria (pedido de Mary).
// East = hora del Este; Central = hora del Centro y de la Montaña; West Coast =
// hora del Pacífico + Alaska y Hawái. En los estados con dos husos, el de la
// mayor parte. Son los 51 del mapa (50 + D. C.), cada uno en una sola región.
const REGIONS = [
  { key: 'east', states: ['CT', 'DC', 'DE', 'FL', 'GA', 'IN', 'KY', 'MA', 'MD', 'ME', 'MI', 'NC', 'NH', 'NJ', 'NY', 'OH', 'PA', 'RI', 'SC', 'VA', 'VT', 'WV'] },
  { key: 'central', states: ['AL', 'AR', 'AZ', 'CO', 'IA', 'ID', 'IL', 'KS', 'LA', 'MN', 'MO', 'MS', 'MT', 'ND', 'NE', 'NM', 'OK', 'SD', 'TN', 'TX', 'UT', 'WI', 'WY'] },
  { key: 'west', states: ['AK', 'CA', 'HI', 'NV', 'OR', 'WA'] },
];

const money = (v) => formatCurrency(v ?? 0, 'en-US');
// Fechas en formato de EE. UU. (MM/DD/YYYY), como el resto de los reportes.
const usDay = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '—';
};
const hourLabel = (h) => `${String(h).padStart(2, '0')}:00`;

const tooltipStyle = {
  background: 'hsl(var(--chart-tooltip-bg))',
  border: '1px solid var(--chart-tooltip-border)',
  borderRadius: 10,
  fontSize: 12,
  padding: '10px 14px',
  color: 'hsl(var(--foreground))',
  boxShadow: 'var(--shadow-md)',
};

function periodParams(p) {
  const params = { preset: p.preset };
  if (p.preset === 'custom') {
    params.from_date = p.from;
    params.to_date = p.to;
  }
  return params;
}

async function downloadXlsx(url, params, filename, T) {
  try {
    const res = await api.get(url, { params, responseType: 'blob' });
    const href = window.URL.createObjectURL(new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }));
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(href);
  } catch (err) {
    toast.error(T.downloadError);
  }
}

function PeriodPicker({ T, presets, value, onChange, testid }) {
  const [from, setFrom] = useState(value.from || '');
  const [to, setTo] = useState(value.to || '');
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex flex-wrap gap-2">
        {presets.map((p) => (
          <Button
            key={p}
            size="sm"
            variant={value.preset === p ? 'default' : 'outline'}
            onClick={() => (p === 'custom'
              ? onChange({ preset: 'custom', from, to, draft: true })
              : onChange({ preset: p }))}
            data-testid={`${testid}-${p}`}
          >
            {T.presets[p]}
          </Button>
        ))}
      </div>
      {value.preset === 'custom' && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-[12px] text-muted-foreground">
            {T.from}
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 w-40" />
          </label>
          <label className="text-[12px] text-muted-foreground">
            {T.to}
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 w-40" />
          </label>
          <Button
            size="sm"
            disabled={!from || !to || from > to}
            onClick={() => onChange({ preset: 'custom', from, to })}
            data-testid={`${testid}-apply`}
          >
            {T.apply}
          </Button>
        </div>
      )}
    </div>
  );
}

function Bars({ data, xKey, yKey = 'total', height = 200, fmtX, fmtY = money, label, seriesName }) {
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey={xKey} tickLine={false} axisLine={false} tickFormatter={fmtX} fontSize={11} />
          <YAxis tickLine={false} axisLine={false} fontSize={11} width={64}
            tickFormatter={(v) => (yKey === 'total' ? `$${Math.round(v).toLocaleString('en-US')}` : v)} />
          <Tooltip
            cursor={{ fill: 'rgba(212,168,67,0.08)' }}
            contentStyle={tooltipStyle}
            labelFormatter={fmtX}
            formatter={(v, _n, item) => [
              `${fmtY(v)} · ${item?.payload?.orders ?? 0} ${label}`,
              seriesName,
            ]}
          />
          <Bar dataKey={yKey} fill={GOLD} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Section({ title, icon: Icon, actions, children, testid }) {
  return (
    <div className="premium-card" style={{ padding: 20 }} data-testid={testid}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[14px] font-semibold">
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" />} {title}
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}

function peak(rows, key) {
  let best = null;
  for (const r of rows || []) {
    if ((r.total || 0) > 0 && (!best || r.total > best.total)) best = r;
  }
  return best ? best[key] : null;
}

// ---------------------------------------------------------------------------
// Ventas por estado
// ---------------------------------------------------------------------------
function StatesTab({ T }) {
  const [period, setPeriod] = useState({ preset: 'this_month' });
  const [selected, setSelected] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const ready = period.preset !== 'custom' || (!period.draft && period.from && period.to);
  const params = periodParams(period);

  const q = useQuery({
    queryKey: ['sales-states', params],
    queryFn: async () => (await api.get('/sales-reports/states', { params })).data,
    enabled: ready,
  });
  const detailQ = useQuery({
    queryKey: ['sales-state', selected, params],
    queryFn: async () => (await api.get(`/sales-reports/states/${selected}`, { params })).data,
    enabled: ready && !!selected,
  });

  const data = q.data;
  const byCode = useMemo(() => Object.fromEntries((data?.states || []).map((s) => [s.code, s])), [data]);
  const ranking = data?.states || [];
  const maxTotal = ranking[0]?.total || 0;
  const d = detailQ.data;
  const fmtPeriod = (p) => (data?.granularity === 'week' ? T.weekOf(usDay(p)) : usDay(p));
  // Fix 63 (2026-10-21): suma de los estados de cada región (mismos datos del mapa).
  const regions = useMemo(() => {
    const national = data?.national?.total || 0;
    return REGIONS.map((r) => {
      const rows = r.states.map((c) => byCode[c]).filter(Boolean);
      const total = rows.reduce((s, x) => s + (x.total || 0), 0);
      const top = rows.filter((x) => x.total > 0).sort((a, b) => b.total - a.total)[0];
      return {
        key: r.key,
        total,
        orders: rows.reduce((s, x) => s + (x.orders || 0), 0),
        pct: national ? (100 * total) / national : 0,
        withSales: rows.filter((x) => x.orders > 0).length,
        of: r.states.length,
        top,
      };
    });
  }, [data, byCode]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PeriodPicker T={T} presets={STATE_PRESETS} value={period} onChange={setPeriod} testid="states-period" />
        <Button
          variant="secondary"
          size="sm"
          disabled={!data || downloading}
          onClick={async () => {
            setDownloading(true);
            await downloadXlsx('/sales-reports/states/export', params,
              `lovi-ventas-por-estado_${data.from_date}_a_${data.to_date}.xlsx`, T);
            setDownloading(false);
          }}
          data-testid="states-download"
        >
          {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
          {T.download}
        </Button>
      </div>

      {q.isLoading && (
        <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> {T.loading}
        </div>
      )}
      {q.isError && <EmptyState message={T.error} testid="states-error" />}

      {data && (
        <>
          <p className="text-[12px] text-muted-foreground" data-testid="states-period-text">
            {T.period(usDay(data.from_date), usDay(data.to_date))}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="states-kpis">
            {/* Fix 53 (2026-10-07): todo lo vendido (con y sin estado), igual que la pestaña Clientes. */}
            <KpiCard index={0} label={T.kpiTotal} value={money((data.all || data.national).total)} icon={DollarSign} tone="success" testid="kpi-states-total" />
            <KpiCard index={1} label={T.kpiOrders} value={(data.all || data.national).orders} icon={ShoppingBag} testid="kpi-states-orders" />
            <KpiCard index={2} label={T.kpiCustomers} value={(data.all || data.national).customers} icon={Users} testid="kpi-states-customers" />
            <KpiCard index={3} label={T.kpiStates} value={T.of51(data.national.states_with_sales)} icon={MapPinned} testid="kpi-states-with-sales" />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
            <div className="lg:col-span-3">
              <Section title={T.mapTitle} icon={MapPinned} testid="states-map">
                <p className="mb-3 text-[12px] text-muted-foreground">{T.mapHelp}</p>
                <UsTileMap
                  data={byCode}
                  selected={selected}
                  onSelect={setSelected}
                  describe={(code, s) => `${s.name || code}: ${money(s.total)} · ${s.orders || 0} ${T.orders.toLowerCase()}`}
                  lessLabel={T.less}
                  moreLabel={T.more}
                />
                {data.unknown.orders > 0 && (
                  <p className="mt-3 text-[11px] text-muted-foreground" data-testid="states-unknown">
                    {T.unknown(data.unknown.orders, money(data.unknown.total))}
                  </p>
                )}
              </Section>
            </div>

            <div className="lg:col-span-2">
              <Section
                title={d ? `${d.name} (${d.code})` : (selected ? T.state : T.regionsTitle)}
                actions={selected && (
                  <Button size="sm" variant="ghost" onClick={() => setSelected(null)} data-testid="state-detail-close">
                    <X className="h-4 w-4" />
                  </Button>
                )}
                testid="state-detail"
              >
                {!selected && (
                  // Fix 63 (2026-10-21): sin estado elegido, ventas por región
                  <div className="space-y-4" data-testid="regions">
                    <p className="text-[11px] text-muted-foreground">{T.regionsHelp}</p>
                    {regions.map((r) => (
                      <div key={r.key} data-testid={`region-${r.key}`}>
                        <div className="flex items-baseline justify-between gap-2">
                          <div>
                            <span className="text-[14px] font-semibold">{T.regionNames[r.key]}</span>
                            <span className="ml-2 text-[11px] text-muted-foreground">{T.regionZones[r.key]}</span>
                          </div>
                          <span className="text-[15px] font-semibold tabular-nums" data-testid={`region-${r.key}-total`}>
                            {money(r.total)}
                          </span>
                        </div>
                        <div className="mt-1.5 h-1.5 w-full rounded bg-muted">
                          <div className="h-1.5 rounded" style={{ width: `${Math.min(100, r.pct)}%`, background: GOLD }} />
                        </div>
                        <div className="mt-1 text-[11px] text-muted-foreground">
                          {r.pct.toFixed(1)}% {T.pctNational} · {r.orders} {T.orders.toLowerCase()} · {T.regionStates(r.withSales, r.of)}
                          {r.top ? ` · ${T.regionTop(r.top.code, money(r.top.total))}` : ''}
                        </div>
                      </div>
                    ))}
                    <p className="text-[12px] text-muted-foreground">{T.pickState}</p>
                  </div>
                )}
                {selected && detailQ.isLoading && (
                  <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> {T.loading}
                  </div>
                )}
                {d && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-[11px] uppercase text-muted-foreground">{T.total}</div>
                        <div className="text-xl font-semibold" data-testid="state-detail-total">{money(d.total)}</div>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase text-muted-foreground">{T.orders}</div>
                        <div className="text-xl font-semibold">{d.orders}</div>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase text-muted-foreground">{T.customers}</div>
                        <div className="text-xl font-semibold">{d.customers}</div>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase text-muted-foreground">{T.pct}</div>
                        <div className="text-xl font-semibold">{d.pct.toFixed(1)}%</div>
                      </div>
                    </div>
                    <p className="text-[12px] text-muted-foreground" data-testid="state-detail-rank">
                      {T.rank(d.rank, d.of)} · {d.pct.toFixed(1)}% {T.pctNational}
                    </p>
                    {d.orders === 0 ? (
                      <p className="text-[13px] text-muted-foreground">{T.noSales}</p>
                    ) : (
                      <>
                        <div>
                          <div className="mb-1 text-[12px] font-medium">
                            {d.granularity === 'week' ? T.salesByWeek : T.salesByDay}
                          </div>
                          <Bars data={d.series} xKey="period" height={160} fmtX={fmtPeriod} label={T.orders.toLowerCase()} seriesName={T.total} />
                        </div>
                        <div>
                          <div className="mb-1 text-[12px] font-medium">{T.salesByHour}</div>
                          <Bars data={d.hours} xKey="hour" height={140} fmtX={hourLabel} label={T.orders.toLowerCase()} seriesName={T.total} />
                        </div>
                        <div>
                          <div className="mb-1 text-[12px] font-medium">{T.salesByWeekday}</div>
                          <Bars data={d.weekdays} xKey="weekday" height={140} fmtX={(i) => T.weekdays[i]} label={T.orders.toLowerCase()} seriesName={T.total} />
                        </div>
                        <div>
                          <div className="mb-1 text-[12px] font-medium">{T.topCustomers}</div>
                          <Table>
                            <TableBody>
                              {d.top_customers.map((c, i) => (
                                <TableRow key={c.tiktok_user_id}>
                                  <TableCell className="w-8 text-xs text-muted-foreground">{i + 1}</TableCell>
                                  <TableCell className="text-xs">{c.name || T.pending}</TableCell>
                                  <TableCell className="text-right text-xs">{money(c.total)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </Section>
            </div>
          </div>

          <Section
            title={T.rankingTitle}
            icon={Trophy}
            actions={(
              <Button size="sm" variant="ghost" onClick={() => setShowAll((v) => !v)} data-testid="states-ranking-toggle">
                {showAll ? T.seeTop : T.seeAll}
              </Button>
            )}
            testid="states-ranking"
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>{T.state}</TableHead>
                  <TableHead className="text-right">{T.total}</TableHead>
                  <TableHead className="text-right">{T.orders}</TableHead>
                  <TableHead className="text-right">{T.pct}</TableHead>
                  <TableHead className="hidden md:table-cell w-1/4" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(showAll ? ranking : ranking.slice(0, 10)).map((s) => (
                  <TableRow
                    key={s.code}
                    className={`cursor-pointer ${selected === s.code ? 'bg-muted/60' : ''}`}
                    onClick={() => setSelected(s.code)}
                    data-testid={`states-ranking-row-${s.code}`}
                  >
                    <TableCell className="text-xs text-muted-foreground">{s.rank}</TableCell>
                    <TableCell className="text-xs">{s.name} <span className="text-muted-foreground">({s.code})</span></TableCell>
                    <TableCell className="text-right text-xs font-medium">{money(s.total)}</TableCell>
                    <TableCell className="text-right text-xs">{s.orders}</TableCell>
                    <TableCell className="text-right text-xs">{s.pct.toFixed(1)}%</TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="h-2 rounded-full bg-muted">
                        <div className="h-2 rounded-full" style={{ width: `${maxTotal ? (100 * s.total) / maxTotal : 0}%`, background: GOLD }} />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>

          <Section title={T.hoursTitle} icon={Clock} testid="states-hours">
            <p className="mb-3 text-[12px] text-muted-foreground">
              {T.hoursHelp(data.timezone)}
              {peak(data.hours, 'hour') !== null && <> · {T.peakHour(hourLabel(peak(data.hours, 'hour')))}</>}
              {peak(data.weekdays, 'weekday') !== null && <> · {T.peakDay(T.weekdaysLong[peak(data.weekdays, 'weekday')])}</>}
            </p>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div>
                <div className="mb-1 text-[12px] font-medium">{T.salesByHour}</div>
                <Bars data={data.hours} xKey="hour" fmtX={hourLabel} label={T.orders.toLowerCase()} seriesName={T.total} />
              </div>
              <div>
                <div className="mb-1 text-[12px] font-medium">{T.salesByWeekday}</div>
                <Bars data={data.weekdays} xKey="weekday" fmtX={(i) => T.weekdays[i]} label={T.orders.toLowerCase()} seriesName={T.total} />
              </div>
            </div>
          </Section>

          <Section title={T.seriesTitle} testid="states-series">
            <div className="mb-1 text-[12px] text-muted-foreground">
              {data.granularity === 'week' ? T.salesByWeek : T.salesByDay}
            </div>
            <Bars data={data.series} xKey="period" fmtX={fmtPeriod} label={T.orders.toLowerCase()} seriesName={T.total} />
          </Section>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Clientes
// ---------------------------------------------------------------------------
function matches(r, s) {
  if (!s) return true;
  const digits = s.replace(/\D/g, '');
  const text = `${r.name || ''} ${r.nickname || ''} ${r.phone || ''}`.toLowerCase();
  if (text.includes(s)) return true;
  return digits.length >= 3 && String(r.phone || '').replace(/\D/g, '').includes(digits);
}

function CustomersTab({ T }) {
  const [period, setPeriod] = useState({ preset: 'all' });
  const [topN, setTopN] = useState(10);
  const [search, setSearch] = useState('');
  const [asc, setAsc] = useState(false);
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [downloading, setDownloading] = useState(false);
  const ready = period.preset !== 'custom' || (!period.draft && period.from && period.to);
  const params = periodParams(period);

  const q = useQuery({
    queryKey: ['sales-customers', params],
    queryFn: async () => (await api.get('/sales-reports/customers', { params })).data,
    enabled: ready,
  });
  const data = q.data;
  const items = data?.items || [];
  const s = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    const rows = items.filter((r) => matches(r, s));
    return asc ? [...rows].reverse() : rows;
  }, [items, s, asc]);
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const current = Math.min(page, pages);
  const pageRows = filtered.slice((current - 1) * pageSize, current * pageSize);
  const top = items.slice(0, topN);
  const best = top[0]?.total || 0;

  const changePeriod = (p) => { setPeriod(p); setPage(1); };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PeriodPicker T={T} presets={CUSTOMER_PRESETS} value={period} onChange={changePeriod} testid="customers-period" />
        <Button
          variant="secondary"
          size="sm"
          disabled={!data || downloading}
          onClick={async () => {
            setDownloading(true);
            const suffix = data.from_date ? `${data.from_date}_a_${data.to_date}` : 'historial';
            await downloadXlsx('/sales-reports/customers/export', params,
              `lovi-ranking-clientes_${suffix}.xlsx`, T);
            setDownloading(false);
          }}
          data-testid="customers-download"
        >
          {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
          {T.download}
        </Button>
      </div>

      {q.isLoading && (
        <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> {T.loading}
        </div>
      )}
      {q.isError && <EmptyState message={T.error} testid="customers-error" />}

      {data && (
        <>
          <p className="text-[12px] text-muted-foreground" data-testid="customers-period-text">
            {T.period(data.from_date && usDay(data.from_date), usDay(data.to_date))}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="customers-kpis">
            <KpiCard index={0} label={T.kpiCustomers} value={data.summary.customers} icon={Users} tone="success" testid="kpi-customers-count" />
            <KpiCard index={1} label={T.kpiOrders} value={data.summary.orders} icon={ShoppingBag} testid="kpi-customers-orders" />
            <KpiCard index={2} label={T.kpiTotal} value={money(data.summary.total)} icon={DollarSign} testid="kpi-customers-total" />
          </div>
          {data.summary.pending_data > 0 && (
            <p className="text-[12px] text-muted-foreground" data-testid="customers-pending-note">
              {T.pendingNote(data.summary.pending_data)}
            </p>
          )}

          {items.length === 0 ? (
            <EmptyState message={T.noCustomers} testid="customers-empty" />
          ) : (
            <>
              <Section
                title={T.topTitle}
                icon={Trophy}
                actions={(
                  <div className="flex gap-1">
                    {[10, 20].map((n) => (
                      <Button key={n} size="sm" variant={topN === n ? 'default' : 'outline'} onClick={() => setTopN(n)} data-testid={`customers-top-${n}`}>
                        {n === 10 ? T.top10 : T.top20}
                      </Button>
                    ))}
                  </div>
                )}
                testid="customers-top"
              >
                <div className="space-y-2">
                  {top.map((r) => (
                    <div key={r.tiktok_user_id} className="flex items-center gap-3" data-testid={`customers-top-row-${r.rank}`}>
                      <div className="w-6 text-right text-[12px] font-semibold text-muted-foreground">{r.rank}</div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="truncate text-[13px]">{r.name || T.pending}</span>
                          <span className="text-[13px] font-semibold">{money(r.total)}</span>
                        </div>
                        <div className="mt-1 h-2 rounded-full bg-muted">
                          <div className="h-2 rounded-full" style={{ width: `${best ? (100 * r.total) / best : 0}%`, background: GOLD }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              <Section title={T.listTitle} icon={Users} testid="customers-list">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="relative w-full md:w-96">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={search}
                      onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                      placeholder={T.search}
                      className="pl-8"
                      data-testid="customers-search"
                    />
                  </div>
                  <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
                    {T.perPage}
                    {PAGE_SIZES.map((n) => (
                      <Button key={n} size="sm" variant={pageSize === n ? 'default' : 'outline'} onClick={() => { setPageSize(n); setPage(1); }} data-testid={`customers-page-size-${n}`}>
                        {n}
                      </Button>
                    ))}
                  </div>
                </div>
                {filtered.length === 0 ? (
                  <p className="py-6 text-center text-[13px] text-muted-foreground">{T.noMatch}</p>
                ) : (
                  <>
                    <div className="overflow-x-auto rounded-lg border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">{T.colRank}</TableHead>
                            <TableHead>{T.colName}</TableHead>
                            <TableHead>{T.colPhone}</TableHead>
                            <TableHead>{T.colState}</TableHead>
                            <TableHead className="text-right">{T.colOrders}</TableHead>
                            <TableHead className="text-right">
                              <button
                                type="button"
                                className="inline-flex items-center gap-1"
                                onClick={() => { setAsc((v) => !v); setPage(1); }}
                                data-testid="customers-sort-total"
                              >
                                {T.colTotal}
                                {asc ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                              </button>
                            </TableHead>
                            <TableHead>{T.colLast}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pageRows.map((r) => (
                            <TableRow key={r.tiktok_user_id} data-testid={`customers-row-${r.rank}`}>
                              <TableCell className="text-xs text-muted-foreground">{r.rank}</TableCell>
                              <TableCell className="text-xs">
                                {r.name || <span className="text-muted-foreground">{T.pending}</span>}
                                {r.nickname && <div className="text-[11px] text-muted-foreground">@{r.nickname}</div>}
                              </TableCell>
                              <TableCell className="font-mono text-xs whitespace-nowrap">{r.phone || '—'}</TableCell>
                              <TableCell className="text-xs">{r.state || '—'}</TableCell>
                              <TableCell className="text-right text-xs">{r.orders}</TableCell>
                              <TableCell className="text-right text-xs font-medium">{money(r.total)}</TableCell>
                              <TableCell className="text-xs whitespace-nowrap">{usDay(r.last_day)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="mt-3 flex items-center justify-between text-[12px] text-muted-foreground">
                      <span data-testid="customers-page-info">{T.pageOf(current, pages)} · {filtered.length}</span>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" disabled={current <= 1} onClick={() => setPage(current - 1)} data-testid="customers-prev">
                          {T.prev}
                        </Button>
                        <Button size="sm" variant="outline" disabled={current >= pages} onClick={() => setPage(current + 1)} data-testid="customers-next">
                          {T.next}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </Section>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default function SalesReportsPage() {
  const { lang } = useLang();
  const T = TEXT[lang === 'en' ? 'en' : 'es'];
  return (
    <div data-testid="sales-reports-page">
      <PageHeader title={T.title} subtitle={T.subtitle} />
      <Tabs defaultValue="states">
        <TabsList className="mb-6">
          <TabsTrigger value="states" data-testid="sales-tab-states">{T.tabStates}</TabsTrigger>
          <TabsTrigger value="customers" data-testid="sales-tab-customers">{T.tabCustomers}</TabsTrigger>
        </TabsList>
        <TabsContent value="states"><StatesTab T={T} /></TabsContent>
        <TabsContent value="customers"><CustomersTab T={T} /></TabsContent>
      </Tabs>
    </div>
  );
}
